import React from 'react';
import { useGetCountries } from 'src/shared/queries/useGetCountries';
import { getCountryName } from 'src/utils/services/common';
import { IAnswerGroup, IAnswerMapping } from '../QuestionnairePrintView.service';
import styles from '../QuestionnairePrintView.module.scss';

interface IWorkLocationTemplateProps {
  answerGroups: IAnswerGroup[];
}

const WORK_LOCATION = {
  ADDRESS_LINE_1: '0',
  ADDRESS_LINE_2: '1',
  FLOOR_APT_SUITE: '2',
  CITY: '3',
  STATE: '4',
  COUNTRY: '5',
  POSTAL_CODE: '6',
  START_DATE: '7',
  END_DATE: '8',
} as const;

const WorkLocationTemplate: React.FC<IWorkLocationTemplateProps> = ({ answerGroups }) => {
  const { data: countries } = useGetCountries();

  const getFieldValue = (answerMapping: Record<string, IAnswerMapping>, fieldKey: string): string => {
    return answerMapping[fieldKey]?.answerForQuestion || '';
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      });
    } catch {
      return dateStr;
    }
  };

  const joinAddressFields = (fields: string[]): string => {
    return fields.filter(Boolean).join(', ');
  };

  const renderCountryLine = (answerMapping: Record<string, IAnswerMapping>): React.ReactNode => {
    const countryCode = getFieldValue(answerMapping, WORK_LOCATION.COUNTRY);

    if (!countries || !countryCode) {
      return null;
    }

    const countryName = getCountryName(countries, countryCode);
    return countryName ? <p>{countryName}</p> : null;
  };

  const renderAddressLine = (answerMapping: Record<string, IAnswerMapping>, fieldKeys: string[]): React.ReactNode => {
    const values = fieldKeys.map((key) => getFieldValue(answerMapping, key));
    const joinedValues = joinAddressFields(values);

    return joinedValues ? <p>{joinedValues}</p> : null;
  };

  return (
    <>
      {answerGroups.map((group, index) => {
        const { answerMapping } = group;
        const startDate = getFieldValue(answerMapping, WORK_LOCATION.START_DATE);
        const endDate = getFieldValue(answerMapping, WORK_LOCATION.END_DATE);

        return (
          <div key={index}>
            <div className={styles['group-type-answer']}>
              <i>Start Date: </i>
              <p className="form-control-static">{formatDate(startDate)}</p>
            </div>

            <div className={styles['group-type-answer']}>
              <i>End Date: </i>
              <p className="form-control-static">{formatDate(endDate)}</p>
            </div>

            <div className={styles['group-type-answer']}>
              <i>Address: </i>
              {renderCountryLine(answerMapping)}
              {renderAddressLine(answerMapping, [
                WORK_LOCATION.ADDRESS_LINE_1,
                WORK_LOCATION.ADDRESS_LINE_2,
                WORK_LOCATION.FLOOR_APT_SUITE,
              ])}
              {renderAddressLine(answerMapping, [WORK_LOCATION.CITY, WORK_LOCATION.STATE, WORK_LOCATION.POSTAL_CODE])}
            </div>
          </div>
        );
      })}
    </>
  );
};

export default WorkLocationTemplate;
